import { useEffect, useState, useImperativeHandle, RefObject } from "react";
import { useMutation } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Tooltip } from '@components/new/Tooltip';
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { Alert, AlertType } from "@components/new/Alert";
import TextInputContainer from "@containers/TextInputContainer";
import { ConfirmEmail, ConfirmState } from "../ConfirmEmail";
// import { addOzonApiData } from "@api/apiClient/sellers";
import { linkVerify, linkSendCode } from "@api/apiClient";
import { FormHandle } from "./types";
import { getErrorMessage } from "@utils/errorMessage";
import InfoIcon from '@assets/icons/outline/info.svg?react';
import styles from './OzonForm.module.scss';

export interface OzonFormType {
  email: string;
  apiKey: string;
  clientId: string;
  name: string;
}

enum steps {
  EMAIL = "email",
  NAME = "name",
  CONFIRM = "confirm",
}

const OzonForm = ({ formRef, onStatusChange, onSuccess, onError, knownEmails }: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
    title?: string,
  }) => void;
  onSuccess: () => void;
  onError: () => void;
  knownEmails: string[];
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(steps.EMAIL);
  const [ticket, setTicket] = useState("");
  const [pin, setPin] = useState("");
  const [confirmState, setConfirmState] = useState<ConfirmState>({ disabled: false, loading: false, });

  const { control, handleSubmit, formState: { isValid }, getValues } = useForm<OzonFormType>({
    defaultValues: {
      email: "",
      apiKey: "",
      clientId: "",
      name: "",
    },
    // mode: 'onBlur',
  });

  const sendLinkMutation = useMutation({
    mutationFn: linkSendCode,
  });

  const verifyMutation = useMutation({
    mutationFn: linkVerify,
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      // ошибка связана с устаревшим или неверным кодом подтверждения - покажем ошибку в форме подтверждения, иначе идем к экрану с сообщением об ошибке
      if (![
        "verification-ticket-invalid-code",
        "verification-ticket-expired",
        "company-already-linked-to-other-account",
      ].find(v => v === error?.response?.data?.code)) {
        onError();
      }
    },
  });

  useEffect(() => {
    onStatusChange({
      disabled: step === steps.CONFIRM ? confirmState.disabled : !isValid,
      isLoading: Boolean(sendLinkMutation.isLoading || verifyMutation.isLoading || confirmState.loading),
      title: (step === steps.NAME) ? t("storesPage.addShopModal.nextBtn") : (
        (step === steps.CONFIRM) ? t("storesPage.addShopModal.confirmBtn") : ""
      ),
    });
  }, [isValid, sendLinkMutation.isLoading, verifyMutation.isLoading, step, confirmState.disabled, confirmState.loading])

  const onSubmit = (form: OzonFormType) => {
    if (step === steps.EMAIL) {
      const isKnownEmail = knownEmails.find(v => (v === form.email));
      if (isKnownEmail) {
        verifyMutation.mutate({
          code: null,
          ticketId: null,
          name: form.name,
          apiKey: form.apiKey,
          clientId: form.clientId,
        });
      } else {
        sendLinkMutation.mutate({
          email: form.email,
          apiKey: form.apiKey,
          clientId: form.clientId,
        }, {
          onSuccess: ({ ticketId, foundCompanies }) => {
            setTicket(ticketId);
            setStep(foundCompanies?.length ? steps.CONFIRM : steps.NAME);
          },
        })
      }
    } else if (step === steps.NAME) {
      setStep(steps.CONFIRM);
    } else if (step === steps.CONFIRM) {
      verifyMutation.mutate({
        code: pin,
        ticketId: ticket,
        name: form.name,
        apiKey: form.apiKey,
        clientId: form.clientId,
      });
    }
  };

  useEffect(() => {
    if (step === steps.CONFIRM && pin.trim().length === 4) submit();
  }, [pin, step]);

  const submit = handleSubmit(onSubmit);
  const back = () => setStep(steps.EMAIL);

  useImperativeHandle(formRef, () => ({
    submit,
    back: step !== steps.EMAIL ? back : undefined,
  }));

  if (step === steps.CONFIRM) {
    const [apiKey, email, clientId] = getValues(["apiKey", "email", "clientId"]);
    return (
      <ConfirmEmail
        email={email}
        apiKey={apiKey}
        clientId={clientId}
        pin={pin}
        setPin={setPin}
        setTicket={setTicket}
        setConfirmState={setConfirmState}
        onSubmit={submit}
        error={verifyMutation.isError ? verifyMutation.error : undefined}
      />
    )
  }

  if (step === steps.NAME) {
    return (
      <form
        className={styles.form}
        onSubmit={submit}
      >
        <TextInputContainer
          control={control}
          name="name"
          title={t('storesPage.addShopModal.name')}
          placeholder={t('storesPage.addShopModal.namePlaceholder')}
          rules={{ required: t("defaultFormErrors.required") }}
          type="text"
          maxLength={999}
        />
      </form>
    )
  }

  return (
    <form
      className={styles.form}
      onSubmit={submit}
    >
      <Alert
        type={AlertType.NEUTRAL}
        title={t('storesPage.addShopModal.ozonInfo')}
      />
      <div className={styles.row}>
        <TextInputContainer
          control={control}
          name="apiKey"
          title={t('storesPage.addShopModal.apiKeyInput')}
          placeholder="API key"
          hasError={sendLinkMutation.isError}
          error={getErrorMessage({ t, error: verifyMutation.error || sendLinkMutation.error })}
          type="text"
          maxLength={999}
        />
        <TextInputContainer
          control={control}
          name="clientId"
          title={t('storesPage.addShopModal.clientIdInput')}
          placeholder="Client ID"
          type="text"
          maxLength={999}
        />
      </div>
      <TextInputContainer
        control={control}
        name="email"
        title={(<span className={styles.emailTitle}>
          <span>{t('storesPage.addShopModal.emailInput')}</span>
          <Tooltip
            action={
              <IconButton
                icon={InfoIcon}
                onClick={() => {}}
                size={IconButtonSize.L}
                type={IconButtonType.ICON}
              />
            }
            position="top"
            text={t('storesPage.addShopModal.emailTooltip')}
          />
        </span>)}
        placeholder="Email"
        rules={{ required: t("defaultFormErrors.required") }}
        type="email"
      />
    </form>
  )
};

export default OzonForm;
